/**
 * <dynamic-message-box>
 * Rotates child [data-dynamic-message-item] with fade out → short pause → fade in.
 * Pauses on: hover (desktop), document hidden, out of viewport.
 * Skips rotation when data-static is set or fewer than 2 items.
 */
if (!customElements.get('dynamic-message-box')) {
  class DynamicMessageBox extends HTMLElement {
    constructor() {
      super();
      this.items = [];
      this.index = 0;
      this.timer = null;
      this.isHovering = false;
      this.isInView = true;
      this.isTransitioning = false;
      this.reducedMotion = false;
      this.fadeMs = 300;
      this.gapMs = 180;
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);
      this.onIntersect = this.onIntersect.bind(this);
    }

    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-dynamic-message-item]'));
      if (this.items.length < 2 || this.hasAttribute('data-static')) return;

      this.intervalMs = Math.max(3, Number(this.dataset.interval) || 4) * 1000;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(this.onIntersect, {
          root: null,
          threshold: 0.2,
        });
        this.observer.observe(this);
      }

      this.start();
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
      if (this.observer) this.observer.disconnect();
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.stop();
      } else {
        this.start();
      }
    }

    onMouseEnter() {
      this.isHovering = true;
      this.stop();
    }

    onMouseLeave() {
      this.isHovering = false;
      this.start();
    }

    onIntersect(entries) {
      const entry = entries[0];
      if (!entry) return;
      this.isInView = entry.isIntersecting;
      if (this.isInView) {
        this.start();
      } else {
        this.stop();
      }
    }

    canRun() {
      return (
        !document.hidden &&
        !this.isHovering &&
        this.isInView &&
        this.items.length > 1 &&
        !this.hasAttribute('data-static')
      );
    }

    start() {
      if (!this.canRun() || this.timer) return;
      this.timer = window.setInterval(() => this.next(), this.intervalMs);
    }

    stop() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    wait(ms) {
      return new Promise((resolve) => window.setTimeout(resolve, ms));
    }

    waitForOpacity(element) {
      return new Promise((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          element.removeEventListener('transitionend', onEnd);
          resolve();
        };
        const onEnd = (event) => {
          if (event.target !== element || event.propertyName !== 'opacity') return;
          finish();
        };
        element.addEventListener('transitionend', onEnd);
        window.setTimeout(finish, this.fadeMs + 80);
      });
    }

    async next() {
      if (this.isTransitioning || !this.canRun()) return;

      const current = this.items[this.index];
      const nextIndex = (this.index + 1) % this.items.length;
      const next = this.items[nextIndex];
      if (!current || !next) return;

      this.isTransitioning = true;

      if (this.reducedMotion) {
        current.classList.remove('is-active', 'is-leaving');
        current.setAttribute('aria-hidden', 'true');
        next.classList.add('is-active');
        next.setAttribute('aria-hidden', 'false');
        this.index = nextIndex;
        this.isTransitioning = false;
        return;
      }

      current.classList.add('is-leaving');
      current.classList.remove('is-active');
      current.setAttribute('aria-hidden', 'true');
      await this.waitForOpacity(current);
      current.classList.remove('is-leaving');

      await this.wait(this.gapMs);
      if (!this.isConnected) return;

      next.setAttribute('aria-hidden', 'false');
      void next.offsetWidth;
      next.classList.add('is-active');
      await this.waitForOpacity(next);

      this.index = nextIndex;
      this.isTransitioning = false;
    }
  }

  customElements.define('dynamic-message-box', DynamicMessageBox);
}
